import { doc, getDoc, setDoc, deleteDoc, collection, query, where, getDocs, updateDoc } from 'firebase/firestore';
import { createUserWithEmailAndPassword, updateProfile, signInWithEmailAndPassword, onAuthStateChanged } from './auth';
import { auth, db, getAdminApiKey } from './config';
// Browser-compatible token hashing using Web Crypto API
const hashToken = async (token) => {
    try {
        const encoder = new TextEncoder();
        const data = encoder.encode(token);
        const hashBuffer = await crypto.subtle.digest('SHA-256', data);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
        return hashHex;
    }
    catch (error) {
        console.error('Failed to hash token:', error);
        // Fallback to simple string manipulation if Web Crypto API fails
        return btoa(token).replace(/[^a-zA-Z0-9]/g, '');
    }
};
const compareToken = async (plainToken, hashedToken) => {
    try {
        const hashedPlainToken = await hashToken(plainToken);
        return hashedPlainToken === hashedToken;
    }
    catch (error) {
        console.error('Failed to compare token:', error);
        return false;
    }
};
/** Generate a cryptographically secure token for invitations (exported for super admin flow). */
export function generateSecureToken() {
    const array = new Uint8Array(32);
    if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
        crypto.getRandomValues(array);
    }
    else {
        for (let i = 0; i < array.length; i++) {
            array[i] = Math.floor(Math.random() * 256);
        }
    }
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
}
/** Hash a token for secure storage (exported for super admin flow). */
export async function hashInvitationToken(token) {
    return hashToken(token);
}
// Helper function to wait for authentication state
const waitForAuthState = () => {
    return new Promise((resolve) => {
        const unsubscribe = onAuthStateChanged((user) => {
            unsubscribe();
            resolve(user);
        });
    });
};
// Email Service Class
export class EmailService {
    // Configure the email service with the function URL (call this from app initialization)
    static configure(functionUrl) {
        EmailService.functionUrl = functionUrl;
        console.log('📧 EmailService: Configured with function URL');
    }
    // Send any email via the Cloud Function
    async sendEmail(templateName, data) {
        try {
            // Try configured URL first (set by app)
            let functionUrl = EmailService.functionUrl;
            // Fallback to environment variables if not configured
            if (!functionUrl) {
                // Try Vite environment variable (web apps)
                // Check if we're in a browser environment (Vite)
                if (typeof window !== 'undefined') {
                    try {
                        // @ts-expect-error - import.meta is available in Vite
                        const viteValue = import.meta.env?.VITE_SENDGRID_FUNCTION_URL;
                        if (viteValue) {
                            functionUrl = viteValue;
                            console.log('📧 EmailService: Using Vite environment variable');
                        }
                    }
                    catch (error) {
                        // Only log in development
                        if (process.env.NODE_ENV === 'development') {
                            console.warn('Failed to access import.meta.env.VITE_SENDGRID_FUNCTION_URL:', error);
                        }
                    }
                }
                // Try Expo environment variable (React Native)
                if (!functionUrl && typeof process !== 'undefined' && process.env?.EXPO_PUBLIC_SENDGRID_FUNCTION_URL) {
                    functionUrl = process.env.EXPO_PUBLIC_SENDGRID_FUNCTION_URL;
                    console.log('📧 EmailService: Using Expo environment variable');
                }
            }
            console.log('📧 EmailService: Function URL:', functionUrl ? '✅ Found' : '❌ Not found');
            if (!functionUrl) {
                console.error('❌ EmailService: No function URL configured. Call EmailService.configure() at app startup.');
                throw new Error('Cloud Function URL is not configured');
            }
            // Log URL domain for debugging (without exposing full URL)
            try {
                const urlObj = new globalThis.URL(functionUrl);
                console.log('📧 EmailService: Sending request to:', `${urlObj.protocol}//${urlObj.host}${urlObj.pathname}`);
            }
            catch {
                console.log('📧 EmailService: Invalid URL format');
            }
            const response = await fetch(functionUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ templateName, data }),
            });
            if (!response.ok) {
                let errorData;
                try {
                    errorData = await response.json();
                }
                catch {
                    errorData = { message: await response.text() };
                }
                const errorMessage = `Cloud Function Error: ${response.status} ${response.statusText} - ${JSON.stringify(errorData)}`;
                console.error('❌ EmailService:', errorMessage);
                throw new Error(errorMessage);
            }
            const result = await response.json();
            console.log('✅ EmailService: Email sent successfully:', result);
            return true;
        }
        catch (error) {
            if (error instanceof TypeError && error.message === 'Failed to fetch') {
                console.error('❌ EmailService: Network error - Failed to fetch. Possible causes:');
                console.error('  1. CORS issue - Cloud Function may not allow requests from this origin');
                console.error('  2. Network connectivity issue');
                console.error('  3. Cloud Function URL may be incorrect or function not deployed');
                console.error('  4. Cloud Function may be down or not accessible');
            }
            console.error('❌ EmailService: Email sending failed:', error);
            throw error;
        }
    }
    // Send municipality invitation email using the new template system
    async sendMunicipalityInvitation(invitation) {
        console.log('📧 EmailService: sendMunicipalityInvitation called with invitation:', {
            id: invitation.id,
            email: invitation.email,
            municipalityName: invitation.municipalityName,
            adminName: invitation.adminName,
            country: invitation.country,
            role: invitation.role,
            token: invitation.token ? `${invitation.token.substring(0, 8)}...` : 'undefined'
        });
        try {
            // Use app.dimotis.com for municipality activation (not fin.dimotis.com)
            const baseUrl = 'https://app.dimotis.com';
            const activationLink = `${baseUrl}/activate?token=${invitation.token}`;
            // Determine template based on role
            const templateName = invitation.role === 'municipality_admin'
                ? 'municipality-invitation'
                : 'municipality-user-invitation';
            const emailData = {
                email: invitation.email,
                contact_person_full_name: invitation.adminName || 'Municipality Administrator',
                municipality_name: invitation.municipalityName ?? 'Municipality',
                country: invitation.country || 'Greece',
                phone: invitation.phone || '',
                invitation_date: invitation.createdAt.toLocaleDateString(),
                activation_link: activationLink,
                // Add role for all municipality invitations
                role: invitation.municipalityRole || (invitation.role === 'municipality_admin' ? 'Administrator' : 'User')
            };
            console.log('📧 EmailService: Using template:', templateName);
            console.log('📧 EmailService: Email data:', emailData);
            return await this.sendEmail(templateName, emailData);
        }
        catch (error) {
            console.error('❌ EmailService: Email sending failed:', error);
            throw error;
        }
    }
    // Send password reset email using the new template system
    async sendPasswordReset(email, resetToken) {
        const resetUrl = `${process.env.EXPO_PUBLIC_APP_URL}/reset-password?token=${resetToken}`;
        const emailData = {
            email,
            resetToken,
            reset_url: resetUrl,
            platform_name: process.env.EXPO_PUBLIC_APP_NAME || 'Dimotis Platform',
        };
        return await this.sendEmail('password-reset', emailData);
    }
    // Request password reset via Cloud Function (generates link and sends via SendGrid)
    async requestPasswordReset(email) {
        try {
            // Get function URL for requestPasswordReset endpoint
            let functionUrl = EmailService.functionUrl;
            // Fallback to environment variables if not configured
            if (!functionUrl) {
                if (typeof window !== 'undefined') {
                    try {
                        // @ts-expect-error - import.meta is available in Vite
                        const viteValue = import.meta.env?.VITE_SENDGRID_FUNCTION_URL;
                        if (viteValue) {
                            functionUrl = viteValue;
                        }
                    }
                    catch {
                        // Ignore
                    }
                }
                if (!functionUrl && typeof process !== 'undefined' && process.env?.EXPO_PUBLIC_SENDGRID_FUNCTION_URL) {
                    functionUrl = process.env.EXPO_PUBLIC_SENDGRID_FUNCTION_URL;
                }
            }
            if (!functionUrl) {
                throw new Error('Cloud Function URL is not configured');
            }
            // Extract base URL and construct requestPasswordReset endpoint
            // Function URL format: https://region-project.cloudfunctions.net/sendEmail
            // We need: https://region-project.cloudfunctions.net/requestPasswordReset
            let resetFunctionUrl;
            try {
                const urlObj = new globalThis.URL(functionUrl);
                // Remove the function name from pathname and add requestPasswordReset
                resetFunctionUrl = `${urlObj.origin}/requestPasswordReset`;
                console.log('📧 EmailService: Constructed reset URL from origin:', resetFunctionUrl);
            }
            catch {
                // Fallback: simple string replacement if URL constructor fails
                resetFunctionUrl = functionUrl.replace('/sendEmail', '/requestPasswordReset');
                console.log('📧 EmailService: Using string replacement fallback:', resetFunctionUrl);
            }
            console.log('📧 EmailService: Requesting password reset for:', email);
            console.log('📧 EmailService: Reset function URL:', resetFunctionUrl);
            console.log('📧 EmailService: Base function URL was:', functionUrl);
            const response = await fetch(resetFunctionUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email }),
            });
            console.log('📧 EmailService: Response status:', response.status, response.statusText);
            if (!response.ok) {
                let errorData;
                try {
                    errorData = await response.json();
                    console.error('❌ EmailService: Password reset failed - Full error response:', JSON.stringify(errorData, null, 2));
                }
                catch {
                    const text = await response.text();
                    errorData = { error: `HTTP ${response.status}: ${text || 'Unknown error'}` };
                    console.error('❌ EmailService: Password reset failed - Text response:', text);
                }
                const errorMessage = errorData.details || errorData.error || `HTTP error! status: ${response.status}`;
                console.error('❌ EmailService: Password reset failed:', errorMessage);
                throw new Error(errorMessage);
            }
            const result = await response.json();
            console.log('✅ EmailService: Password reset requested successfully');
            return result.success === true;
        }
        catch (error) {
            if (error instanceof TypeError && error.message === 'Failed to fetch') {
                console.error('❌ EmailService: Network error - Failed to fetch. Possible causes:');
                console.error('  1. CORS issue - Cloud Function may not allow requests from this origin');
                console.error('  2. Network connectivity issue');
                console.error('  3. Cloud Function URL may be incorrect');
                console.error('  4. Cloud Function may be down or not accessible');
            }
            console.error('❌ EmailService: Password reset request failed:', error);
            throw error;
        }
    }
}
// Invitation Service Class
export class InvitationService {
    constructor() {
        this.emailService = new EmailService();
    }
    // Generate secure token using cryptographically secure random generation
    generateToken() {
        // Use crypto.getRandomValues for cryptographically secure random generation
        const array = new Uint8Array(32);
        // Use Web Crypto API (available in both browser and Node.js 15+)
        if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
            crypto.getRandomValues(array);
        }
        else {
            // Fallback for older environments - generate pseudo-random values
            for (let i = 0; i < array.length; i++) {
                array[i] = Math.floor(Math.random() * 256);
            }
        }
        // Convert to hex string for consistent format
        return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
    }
    // Create invitation
    async createInvitation(email, municipalityName, municipalityId, role = 'municipality_admin', // Canonical lowercase
    adminName, // DEPRECATED: Keep for backward compatibility
    country, phone, municipalityRole, // roleId from municipality_roles (lowercase preferred)
    name, // DEPRECATED: Use firstName instead
    surname, // DEPRECATED: Use lastName instead
    firstName, // NEW: First name of person being invited
    lastName // NEW: Last name of person being invited
    ) {
        console.log('🎯 InvitationService: createInvitation called with:', {
            email,
            municipalityName,
            municipalityId,
            role,
            adminName,
            country,
            phone
        });
        try {
            // Input validation and sanitization
            if (!email || typeof email !== 'string') {
                throw new Error('Invalid email address');
            }
            // Sanitize email
            const sanitizedEmail = email.toLowerCase().trim();
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(sanitizedEmail)) {
                throw new Error('Invalid email format');
            }
            if (!municipalityName || typeof municipalityName !== 'string') {
                throw new Error('Invalid municipality name');
            }
            // Sanitize municipality name
            const sanitizedMunicipalityName = municipalityName.trim().replace(/[<>]/g, '');
            if (!municipalityId || typeof municipalityId !== 'string') {
                throw new Error('Invalid municipality ID');
            }
            if (!['municipality_admin', 'municipality_user'].includes(role)) {
                throw new Error('Invalid role');
            }
            // Sanitize optional fields
            const sanitizedFirstName = firstName ? firstName.trim().replace(/[<>]/g, '') : undefined;
            const sanitizedLastName = lastName ? lastName.trim().replace(/[<>]/g, '') : undefined;
            // adminName = person who sent the invitation (super admin)
            // firstName/lastName = person being invited (municipality admin)
            // Priority: adminName (if provided) > firstName+lastName (for backward compatibility)
            let sanitizedAdminName;
            if (adminName) {
                // adminName is explicitly provided - use it (this is the person who sent the invitation)
                sanitizedAdminName = adminName.trim().replace(/[<>]/g, '');
            }
            else if (sanitizedFirstName && sanitizedLastName) {
                // Fallback to firstName+lastName for backward compatibility
                sanitizedAdminName = `${sanitizedFirstName} ${sanitizedLastName}`.trim();
            }
            else if (name && surname) {
                // Fallback to name+surname for backward compatibility
                sanitizedAdminName = `${name.trim()} ${surname.trim()}`.replace(/[<>]/g, '');
            }
            // Keep deprecated fields for backward compatibility
            const sanitizedName = name ? name.trim().replace(/[<>]/g, '') : undefined;
            const sanitizedSurname = surname ? surname.trim().replace(/[<>]/g, '') : undefined;
            const sanitizedCountry = country ? country.trim().replace(/[<>]/g, '') : undefined;
            const sanitizedPhone = phone ? phone.trim().replace(/[^0-9+\-\s()]/g, '') : undefined;
            // Normalize municipalityRole to lowercase for canonical format
            const normalizedMunicipalityRole = municipalityRole
                ? municipalityRole.toLowerCase()
                : undefined;
            // Check if invitation already exists
            console.log('🎯 InvitationService: Checking for existing invitation...');
            const existingInvitation = await this.getInvitationByEmail(sanitizedEmail);
            if (existingInvitation && existingInvitation.status === 'pending') {
                console.warn('⚠️ InvitationService: Found existing pending invitation for email:', sanitizedEmail);
                console.log('🎯 InvitationService: Deleting existing pending invitation to create new one...');
                await this.deleteInvitation(existingInvitation.id);
                console.log('✅ InvitationService: Existing invitation deleted successfully');
            }
            console.log('🎯 InvitationService: No existing invitation found, creating new one...');
            // Create invitation data with sanitized values
            const invitation = {
                id: this.generateToken(),
                email: sanitizedEmail,
                municipalityName: sanitizedMunicipalityName,
                municipalityId,
                role,
                status: 'pending',
                token: this.generateToken(),
                expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // 7 days
                createdAt: new Date(),
                // NEW: Store separate first and last names
                firstName: sanitizedFirstName,
                lastName: sanitizedLastName,
                // For backward compatibility with email templates and existing code
                adminName: sanitizedAdminName,
                name: sanitizedName,
                surname: sanitizedSurname,
                country: sanitizedCountry,
                phone: sanitizedPhone,
                municipalityRole: normalizedMunicipalityRole, // Use normalized lowercase value
            };
            console.log('🎯 InvitationService: Created invitation data:', {
                id: invitation.id,
                email: invitation.email,
                municipalityName: invitation.municipalityName,
                token: invitation.token ? `${invitation.token.substring(0, 8)}...` : 'undefined',
                expiresAt: invitation.expiresAt,
                adminName: invitation.adminName,
                country: invitation.country
            });
            // Hash the token before storing in Firestore
            const hashedToken = await hashToken(invitation.token);
            console.log('🔐 InvitationService: Token hashed successfully');
            // Save to Firestore
            console.log('🎯 InvitationService: Saving invitation to Firestore...');
            // Filter out undefined values and prepare data for Firestore
            const firestoreData = {
                id: invitation.id,
                email: invitation.email,
                municipalityName: invitation.municipalityName ?? '',
                municipalityId: invitation.municipalityId ?? '',
                role: invitation.role,
                status: invitation.status,
                token: hashedToken, // Store hashed token
                tokenPlain: invitation.token, // Store plain token temporarily for email
                expiresAt: invitation.expiresAt,
                createdAt: invitation.createdAt,
            };
            // Only add optional fields if they have values
            if (invitation.firstName)
                firestoreData.firstName = invitation.firstName;
            if (invitation.lastName)
                firestoreData.lastName = invitation.lastName;
            if (invitation.adminName)
                firestoreData.adminName = invitation.adminName;
            if (invitation.name)
                firestoreData.name = invitation.name;
            if (invitation.surname)
                firestoreData.surname = invitation.surname;
            if (invitation.country)
                firestoreData.country = invitation.country;
            if (invitation.phone)
                firestoreData.phone = invitation.phone;
            if (invitation.municipalityRole)
                firestoreData.municipalityRole = invitation.municipalityRole;
            await setDoc(doc(db, 'invitations', invitation.id), firestoreData);
            console.log('✅ InvitationService: Invitation saved to Firestore successfully');
            // Send invitation email using SendGrid template (optional)
            console.log('🎯 InvitationService: Sending invitation email...');
            try {
                await this.emailService.sendMunicipalityInvitation(invitation);
                console.log('✅ InvitationService: Invitation email sent successfully');
            }
            catch (emailError) {
                console.warn('⚠️ InvitationService: Email sending failed, but invitation was created successfully:', emailError);
                // Don't throw the error - invitation creation was successful
            }
            console.log('🎯 InvitationService: Invitation creation completed successfully');
            return invitation;
        }
        catch (error) {
            console.error('❌ InvitationService: Error creating invitation:', {
                error: error,
                message: error instanceof Error ? error.message : 'Unknown error',
                stack: error instanceof Error ? error.stack : undefined
            });
            // Sanitize error messages in production
            const isProduction = process.env.NODE_ENV === 'production';
            if (isProduction) {
                throw new Error('Failed to create invitation. Please try again later.');
            }
            else {
                throw error;
            }
        }
    }
    // Get invitation by token
    async getInvitationByToken(token) {
        try {
            const invitationsRef = collection(db, 'invitations');
            // Get all invitations and compare hashed tokens
            const querySnapshot = await getDocs(invitationsRef);
            for (const docSnap of querySnapshot.docs) {
                const data = docSnap.data();
                if (!data.expiresAt || !data.token)
                    continue;
                // Skip expired invitations (no per-doc log to avoid console noise)
                const expiresAt = data.expiresAt?.toDate?.();
                if (expiresAt && expiresAt < new Date())
                    continue;
                // Compare hashed token
                if (await compareToken(token, data.token)) {
                    return {
                        ...data,
                        id: data.id || docSnap.id,
                        token: data.tokenPlain || token,
                        expiresAt: expiresAt || data.expiresAt,
                        createdAt: data.createdAt?.toDate?.() || data.createdAt,
                        acceptedAt: data.acceptedAt?.toDate?.() || data.acceptedAt,
                    };
                }
            }
            return null;
        }
        catch (error) {
            console.error('Error getting invitation:', error);
            throw error;
        }
    }
    // Get invitation by email
    async getInvitationByEmail(email) {
        try {
            const invitationsRef = collection(db, 'invitations');
            const q = query(invitationsRef, where('email', '==', email));
            const querySnapshot = await getDocs(q);
            if (querySnapshot.empty) {
                return null;
            }
            const doc = querySnapshot.docs[0];
            const data = doc.data();
            return {
                ...data,
                expiresAt: data.expiresAt.toDate(),
                createdAt: data.createdAt.toDate(),
                acceptedAt: data.acceptedAt?.toDate(),
            };
        }
        catch (error) {
            console.error('Error getting invitation by email:', error);
            throw error;
        }
    }
    // Update invitation status
    async updateInvitationStatus(invitationId, status, acceptedBy) {
        console.log('🔍 DEBUG: updateInvitationStatus called with:', { invitationId, status, acceptedBy });
        try {
            const updateData = { status };
            if (status === 'accepted' && acceptedBy) {
                updateData.acceptedAt = new Date();
                updateData.acceptedBy = acceptedBy;
            }
            console.log('🔍 DEBUG: Update data prepared:', updateData);
            console.log('🔍 DEBUG: Current auth state:', auth.currentUser ? { uid: auth.currentUser.uid, email: auth.currentUser.email } : 'NOT AUTHENTICATED');
            await updateDoc(doc(db, 'invitations', invitationId), updateData);
            console.log('🔍 DEBUG: Invitation status updated successfully');
        }
        catch (error) {
            console.error('❌ DEBUG: Error updating invitation status:', {
                error: error,
                message: error instanceof Error ? error.message : 'Unknown error',
                code: error instanceof Error && 'code' in error ? error.code : 'No code'
            });
            throw error;
        }
    }
    // Update municipality status
    async updateMunicipalityStatus(municipalityId, status) {
        console.log('🔍 DEBUG: updateMunicipalityStatus called with:', { municipalityId, status });
        try {
            const updateData = {
                status,
                updatedAt: new Date()
            };
            // Removed: adminId - user data should not be stored in municipality records
            console.log('🔍 DEBUG: Municipality update data prepared:', updateData);
            await updateDoc(doc(db, 'municipalities', municipalityId), updateData);
            console.log('🔍 DEBUG: Municipality status updated successfully');
        }
        catch (error) {
            console.error('❌ DEBUG: Error updating municipality status:', {
                error: error,
                message: error instanceof Error ? error.message : 'Unknown error',
                code: error instanceof Error && 'code' in error ? error.code : 'No code'
            });
            throw error;
        }
    }
    // Accept invitation and create user
    async acceptInvitation(token, password) {
        console.log('🔍 DEBUG: acceptInvitation started with token:', token.substring(0, 8) + '...');
        try {
            console.log('🔍 DEBUG: Getting invitation by token...');
            const invitation = await this.getInvitationByToken(token);
            console.log('🔍 DEBUG: Invitation retrieved:', invitation ? 'SUCCESS' : 'NOT FOUND');
            if (!invitation) {
                throw new Error('Invalid or expired invitation token');
            }
            if (invitation.status !== 'pending') {
                throw new Error('Invitation has already been used or expired');
            }
            console.log('🔍 DEBUG: Invitation is valid and pending');
            let user;
            const inviteeFirstName = invitation.firstName || invitation.name || '';
            const inviteeLastName = invitation.lastName || invitation.surname || '';
            const displayName = inviteeFirstName && inviteeLastName
                ? `${inviteeFirstName} ${inviteeLastName}`.trim()
                : invitation.email.split('@')[0];
            // Firestore validateUserData requires name.size() > 0 and surname.size() > 0
            const safeName = (inviteeFirstName || invitation.email.split('@')[0] || 'User').trim() || 'User';
            const safeSurname = (inviteeLastName || 'User').trim() || 'User';
            const isSuperAdminInvitation = invitation.role === 'super_admin';
            try {
                console.log('🔍 DEBUG: Attempting to create new user with email:', invitation.email);
                const userCredential = await createUserWithEmailAndPassword(auth, invitation.email, password);
                user = userCredential.user;
                console.log('🔍 DEBUG: User created successfully, UID:', user.uid);
                await updateProfile(user, { displayName });
                console.log('🔍 DEBUG: User profile updated successfully');
                let userData;
                if (isSuperAdminInvitation) {
                    userData = {
                        id: user.uid,
                        email: invitation.email,
                        displayName,
                        name: safeName,
                        surname: safeSurname,
                        role: 'super_admin',
                        superAdminRole: invitation.superAdminRole || 'super_admin',
                        status: 'active',
                        municipalityAssignments: [],
                        assignedMunicipalityIds: [],
                        invitedBy: invitation.invitedBy || 'System',
                        invitedAt: invitation.createdAt,
                        createdAt: new Date().toISOString(),
                        updatedAt: new Date().toISOString(),
                        phoneNumber: invitation.phoneNumber || invitation.phone || '',
                    };
                }
                else {
                    userData = {
                        id: user.uid,
                        email: invitation.email,
                        displayName,
                        name: safeName,
                        surname: safeSurname,
                        role: invitation.role,
                        municipalityId: invitation.municipalityId,
                        municipalityName: invitation.municipalityName,
                        municipalityRole: invitation.municipalityRole || 'support',
                        status: 'active',
                        invitedBy: invitation.adminName || 'System',
                        invitedAt: invitation.createdAt,
                        createdAt: new Date().toISOString(),
                        updatedAt: new Date().toISOString(),
                        isActive: true,
                    };
                }
                // Create user document BEFORE auth state propagates to AuthContext
                // AuthContext's onAuthStateChanged runs when user signs in; it needs the doc to exist
                console.log('🔍 DEBUG: Creating user document in Firestore (before auth propagation)...');
                await setDoc(doc(db, 'users', user.uid), userData);
                console.log('🔍 DEBUG: User document created in Firestore successfully');
                // Set custom claims for the user (required for Storage and other permissions)
                console.log('🔍 DEBUG: Setting custom claims for user...');
                try {
                    const setRoleUrl = 'https://us-central1-municipalities-v3.cloudfunctions.net/setUserRole';
                    const adminApiKey = getAdminApiKey();
                    if (!adminApiKey) {
                        console.warn('⚠️ VITE_ADMIN_API_KEY not set – custom claims will not be applied. Add to .env.');
                    }
                    else {
                        const claimsPayload = {
                            uid: user.uid,
                            role: userData.role,
                        };
                        if (userData.municipalityId) {
                            claimsPayload.municipalityId = userData.municipalityId;
                        }
                        if (userData.municipalityRole) {
                            claimsPayload.municipalityRole = userData.municipalityRole;
                        }
                        const claimsResponse = await fetch(setRoleUrl, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'X-Admin-Key': adminApiKey
                            },
                            body: JSON.stringify(claimsPayload)
                        });
                        if (claimsResponse.ok) {
                            console.log('✅ DEBUG: Custom claims set successfully');
                            // Force token refresh so the user gets the new claims immediately
                            if (auth.currentUser) {
                                await auth.currentUser.getIdToken(true);
                                console.log('✅ DEBUG: Token refreshed with new claims');
                            }
                        }
                        else {
                            const errorText = await claimsResponse.text();
                            console.warn('⚠️ DEBUG: Failed to set custom claims:', errorText);
                        }
                    }
                }
                catch (claimsError) {
                    console.error('❌ DEBUG: Error setting custom claims:', claimsError);
                    // Don't fail the entire operation if claims setting fails
                }
            }
            catch (error) {
                console.log('🔍 DEBUG: Error in user creation/signup:', error.code, error.message);
                // If user already exists, sign them in and update their profile
                if (error.code === 'auth/email-already-in-use') {
                    console.log('🔍 DEBUG: User already exists, signing in...');
                    const userCredential = await signInWithEmailAndPassword(auth, invitation.email, password);
                    user = userCredential.user;
                    console.log('🔍 DEBUG: User signed in successfully, UID:', user.uid);
                    await updateProfile(user, { displayName });
                    console.log('🔍 DEBUG: User profile updated successfully');
                    const existingUserData = isSuperAdminInvitation
                        ? {
                            id: user.uid,
                            email: invitation.email,
                            displayName,
                            name: safeName,
                            surname: safeSurname,
                            role: 'super_admin',
                            superAdminRole: invitation.superAdminRole || 'super_admin',
                            status: 'active',
                            municipalityAssignments: [],
                            assignedMunicipalityIds: [],
                            invitedBy: invitation.invitedBy || 'System',
                            invitedAt: invitation.createdAt,
                            createdAt: new Date().toISOString(),
                            updatedAt: new Date().toISOString(),
                            phoneNumber: invitation.phoneNumber || invitation.phone || '',
                        }
                        : {
                            id: user.uid,
                            email: invitation.email,
                            displayName,
                            name: safeName,
                            surname: safeSurname,
                            role: invitation.role,
                            municipalityId: invitation.municipalityId,
                            municipalityName: invitation.municipalityName,
                            municipalityRole: invitation.municipalityRole || 'support',
                            status: 'active',
                            invitedBy: invitation.adminName || 'System',
                            invitedAt: invitation.createdAt,
                            createdAt: new Date().toISOString(),
                            updatedAt: new Date().toISOString(),
                            isActive: true,
                        };
                    console.log('🔍 DEBUG: User data prepared for update:', { uid: existingUserData.id, email: existingUserData.email, role: existingUserData.role });
                    const authenticatedUser = await waitForAuthState();
                    console.log('🔍 DEBUG: Authentication state confirmed (existing user):', {
                        isAuthenticated: !!authenticatedUser,
                        currentUserUID: authenticatedUser?.uid,
                        targetDocumentUID: user.uid,
                        match: authenticatedUser?.uid === user.uid
                    });
                    await setDoc(doc(db, 'users', user.uid), existingUserData, { merge: true });
                    console.log('🔍 DEBUG: User document updated in Firestore successfully');
                    console.log('🔍 DEBUG: Setting custom claims for existing user...');
                    try {
                        const setRoleUrl = 'https://us-central1-municipalities-v3.cloudfunctions.net/setUserRole';
                        const adminApiKey = getAdminApiKey();
                        if (adminApiKey) {
                            const ed = existingUserData;
                            const claimsPayload = { uid: user.uid, role: ed.role };
                            if (ed.municipalityId)
                                claimsPayload.municipalityId = ed.municipalityId;
                            if (ed.municipalityRole)
                                claimsPayload.municipalityRole = ed.municipalityRole;
                            const claimsResponse = await fetch(setRoleUrl, {
                                method: 'POST',
                                headers: {
                                    'Content-Type': 'application/json',
                                    'X-Admin-Key': adminApiKey
                                },
                                body: JSON.stringify(claimsPayload)
                            });
                            if (claimsResponse.ok) {
                                console.log('✅ DEBUG: Custom claims set successfully for existing user');
                                if (auth.currentUser) {
                                    await auth.currentUser.getIdToken(true);
                                    console.log('✅ DEBUG: Token refreshed with new claims');
                                }
                            }
                            else {
                                const errorText = await claimsResponse.text();
                                console.warn('⚠️ DEBUG: Failed to set custom claims for existing user:', errorText);
                            }
                        }
                        else {
                            console.warn('⚠️ VITE_ADMIN_API_KEY not set – custom claims will not be applied. Add to .env.');
                        }
                    }
                    catch (claimsError) {
                        console.error('❌ DEBUG: Error setting custom claims for existing user:', claimsError);
                        // Don't fail the entire operation if claims setting fails
                    }
                }
                else {
                    console.log('🔍 DEBUG: Non-email-already-in-use error, throwing:', error);
                    throw error;
                }
            }
            // Ensure user is still authenticated before updating invitation
            console.log('🔍 DEBUG: Ensuring user is authenticated before updating invitation...');
            if (!auth.currentUser) {
                console.log('🔍 DEBUG: User not authenticated, signing in again...');
                const userCredential = await signInWithEmailAndPassword(auth, invitation.email, password);
                user = userCredential.user;
            }
            console.log('🔍 DEBUG: Updating invitation status to accepted...');
            // Update invitation status
            await this.updateInvitationStatus(invitation.id, 'accepted', user.uid);
            console.log('🔍 DEBUG: Invitation status updated successfully');
            // Only update municipality status if it's a municipality invitation (not super_admin)
            if (!isSuperAdminInvitation && invitation.municipalityId) {
                console.log('🔍 DEBUG: Checking if municipality status needs updating...');
                try {
                    const municipalityRef = doc(db, 'municipalities', invitation.municipalityId);
                    const municipalitySnap = await getDoc(municipalityRef);
                    if (municipalitySnap.exists()) {
                        const municipalityData = municipalitySnap.data();
                        const currentStatus = municipalityData.status;
                        console.log('🔍 DEBUG: Current municipality status:', currentStatus);
                        // Only update to active if currently pending (first admin activation)
                        if (currentStatus === 'pending') {
                            console.log('🔍 DEBUG: Municipality is pending, updating to active...');
                            await this.updateMunicipalityStatus(invitation.municipalityId, 'active');
                            console.log('🔍 DEBUG: Municipality status updated to active successfully');
                        }
                        else {
                            console.log('🔍 DEBUG: Municipality status is', currentStatus, ', skipping status update (only pending can be activated)');
                        }
                    }
                }
                catch (municipalityError) {
                    console.warn('⚠️ DEBUG: Could not update municipality status:', municipalityError instanceof Error ? municipalityError.message : 'Unknown error');
                }
            }
            console.log('🔍 DEBUG: acceptInvitation completed successfully');
            return { user, invitation };
        }
        catch (error) {
            console.error('❌ DEBUG: Error accepting invitation:', {
                error: error,
                message: error instanceof Error ? error.message : 'Unknown error',
                code: error instanceof Error && 'code' in error ? error.code : 'No code',
                stack: error instanceof Error ? error.stack : undefined
            });
            throw error;
        }
    }
    // Get all invitations for a municipality
    async getMunicipalityInvitations(municipalityId) {
        try {
            const invitationsRef = collection(db, 'invitations');
            const q = query(invitationsRef, where('municipalityId', '==', municipalityId));
            const querySnapshot = await getDocs(q);
            return querySnapshot.docs.map(doc => {
                const data = doc.data();
                return {
                    ...data,
                    expiresAt: data.expiresAt.toDate(),
                    createdAt: data.createdAt.toDate(),
                    acceptedAt: data.acceptedAt?.toDate(),
                };
            });
        }
        catch (error) {
            console.error('Error getting municipality invitations:', error);
            throw error;
        }
    }
    // Delete invitation
    async deleteInvitation(invitationId) {
        try {
            await deleteDoc(doc(db, 'invitations', invitationId));
        }
        catch (error) {
            console.error('Error deleting invitation:', error);
            throw error;
        }
    }
}
// Export instances
export const emailService = new EmailService();
export const invitationService = new InvitationService();
